import React, { useState, useEffect } from 'react';
import { styled } from 'linaria/react';
import { theme } from '../../../Theme';
import { Range } from 'react-range';
import { useElevate } from '../../ElevateContext';
import { useLocation } from 'react-router';

const PriceRangeSlider = styled('div')`
  padding: 0 3rem;
  border-radius: 12px;
  margin: 12px 0;

  .dual-range-container {
    position: relative;
    margin: 16px 0;
    padding: 48px 20px 20px;
    height: 80px;
    display: flex;
    align-items: center;
  }

  .range-track {
    position: absolute;
    left: 0;
    right: 0;
    height: 8px;
    background: ${theme.colors.lightgrey};
    border-radius: 4px;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .range-progress {
    position: absolute;
    height: 8px;
    background: linear-gradient(
      90deg,
      ${theme.colors.primary},
      ${theme.colors.primary}dd
    );
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    transition: all 0.3s ease;
  }

  .range-thumb-wrapper {
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: center;
    top: 50%;
    transform: translate(-50%, -50%);

    &.min-thumb {
      z-index: 2;
    }

    &.max-thumb {
      z-index: 3;
    }
  }

  .thumb-value {
    position: absolute;
    bottom: calc(100% + 8px);
    white-space: nowrap;
    font-size: 0.875rem;
    font-weight: ${theme.fontWeights.semibold};
    color: ${theme.colors.primary};
    background: white;
    padding: 6px 10px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    border: 2px solid ${theme.colors.primary};
    pointer-events: none;
    transition: all 0.2s ease;

    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-top: 6px solid ${theme.colors.primary};
    }
  }

  .range-thumb {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: ${theme.colors.primary};
    border: 4px solid white;
    cursor: grab;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3), 0 1px 4px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.15);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4), 0 2px 6px rgba(0, 0, 0, 0.25);

      & ~ .thumb-value {
        transform: scale(1.05);
        box-shadow: 0 3px 12px rgba(0, 0, 0, 0.18);
      }
    }

    &:active,
    &.dragging {
      cursor: grabbing;
      transform: scale(1.05);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
    }
  }

  .range-error {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 50px;
    color: ${theme.colors.darkgrey};
    font-size: 0.9rem;
    font-style: italic;
  }
`;

const parseBound = value => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Clamp thumbs into [min, max]. Null when Elevate has no range (empty result). */
const getSafeRange = (minValue, maxValue, urlMin, urlMax) => {
  const boundMin = parseBound(minValue);
  const boundMax = parseBound(maxValue);
  if (boundMin === null || boundMax === null || boundMax <= boundMin) {
    return null;
  }

  const parsedMin = parseBound(urlMin);
  const parsedMax = parseBound(urlMax);
  let min = Math.min(Math.max(parsedMin ?? boundMin, boundMin), boundMax);
  let max = Math.min(Math.max(parsedMax ?? boundMax, boundMin), boundMax);
  if (min >= max) {
    min = boundMin;
    max = boundMax;
  }

  return { boundMin, boundMax, values: [min, max] };
};

export const RangeFacet = ({ minValue, maxValue, clickCallback, ...props }) => {
  const location = useLocation();
  const { queryStringToObject } = useElevate();
  const params = queryStringToObject(location.search);

  const safeRange = getSafeRange(
    minValue,
    maxValue,
    params[`f.${props.id}.min`],
    params[`f.${props.id}.max`]
  );

  const [values, setValues] = useState(safeRange?.values ?? [0, 1]);

  useEffect(() => {
    if (safeRange) {
      setValues(safeRange.values);
    }
  }, [safeRange?.boundMin, safeRange?.boundMax, safeRange?.values[0], safeRange?.values[1]]);

  const handleRangeChange = newValues => {
    if (newValues.length === 2 && newValues[0] < newValues[1]) {
      setValues(newValues);
    }
  };

  const generateRangeValues = () => {
    const [min, max] = values;
    let tempMin;
    let tempMax;

    if (min !== safeRange.boundMin) {
      tempMin = min;
    }

    if (max !== safeRange.boundMax) {
      tempMax = max;
    }

    return { min: tempMin, max: tempMax, id: props.id };
  };

  const handleOnFinalChange = () => {
    if (!safeRange) return;
    clickCallback(generateRangeValues());
  };

  const valuesInBounds =
    safeRange &&
    values[0] >= safeRange.boundMin &&
    values[1] <= safeRange.boundMax &&
    values[0] < values[1];
  const sliderValues = valuesInBounds ? values : safeRange?.values;

  return (
    <PriceRangeSlider>
      <div className="dual-range-container">
        {safeRange && sliderValues ? (
          <Range
            step={1}
            min={safeRange.boundMin}
            max={safeRange.boundMax}
            values={sliderValues}
            onChange={handleRangeChange}
            onFinalChange={handleOnFinalChange}
            renderTrack={({ props: trackProps, children }) => (
              <div {...trackProps} className="range-track">
                <div
                  className="range-progress"
                  style={{
                    left: `${
                      ((sliderValues[0] - safeRange.boundMin) /
                        (safeRange.boundMax - safeRange.boundMin)) *
                      100
                    }%`,
                    width: `${
                      ((sliderValues[1] - sliderValues[0]) /
                        (safeRange.boundMax - safeRange.boundMin)) *
                      100
                    }%`
                  }}
                />
                {children}
              </div>
            )}
            renderThumb={({ props: thumbProps, index }) => (
              <div
                {...thumbProps}
                className={`range-thumb-wrapper ${
                  index === 0 ? 'min-thumb' : 'max-thumb'
                }`}
                aria-label={index === 0 ? 'Minimum price' : 'Maximum price'}
              >
                <div className="thumb-value">{sliderValues[index]}</div>
                <div className="range-thumb" />
              </div>
            )}
          />
        ) : (
          <div className="range-error">
            <span>Price range not available</span>
          </div>
        )}
      </div>
    </PriceRangeSlider>
  );
};
